import type {
	ShakaPlayer,
	ShakaPlayerConfiguration
} from '~/composables/useHlsPlayer/types';

import { loadShaka } from '~/composables/useHlsPlayer/shakaLoader';

import { isIOSDevice } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

import { getQualityConstraints } from './bandwidthDetector';

const BUFFER_BEHIND = 5;
const REBUFFER_GOAL = 2;

export interface CreatePlayerOptions {
	/** Use lighter buffer/ABR settings for preloading (not actively playing) */
	isPreload?: boolean;
	/** Full config override — when provided, ignores isPreload and uses this config directly */
	config?: ShakaPlayerConfiguration;
	/** Start playback from this position (seconds). Avoids buffering from 0 then seeking. */
	startTime?: number;
}

function buildPlayerConfig(isPreload: boolean): ShakaPlayerConfiguration {
	const isIOS = isIOSDevice;

	// Get quality constraints based on real bandwidth
	const quality = getQualityConstraints(isPreload);

	const config: any = {
		streaming: {
			bufferingGoal: quality.bufferingGoal,
			rebufferingGoal: REBUFFER_GOAL,
			bufferBehind: BUFFER_BEHIND,
			retryParameters: {
				maxAttempts: 2,
				baseDelay: 500,
				backoffFactor: 1.5,
				timeout: 10000
			}
		},
		abr: {
			enabled: true,
			defaultBandwidthEstimate: quality.bandwidthEstimate,
			switchInterval: 4,
			bandwidthUpgradeTarget: 0.85,
			bandwidthDowngradeTarget: 0.95,
			restrictions: {
				maxHeight: quality.maxHeight
			}
		}
	};

	// Only on iOS: use Safari's native HLS (Shaka 4.16.13+)
	// This avoids MSE conflicts and reduces JS overhead
	if (isIOS) {
		config.streaming.preferNativeHls = true;
		// Disable ABR on iOS since native player handles it
		config.abr.enabled = false;
	}

	return config;
}

export async function createPlayer(
	videoUrl: string,
	videoElement: HTMLVideoElement,
	options: CreatePlayerOptions = {}
): Promise<ShakaPlayer> {
	const isHLS = videoUrl.includes('.m3u8');
	const isPreload = options.isPreload ?? false;

	const existing = (videoElement as HTMLVideoElement & { shaka?: ShakaPlayer })
		.shaka;
	if (existing) {
		await existing.destroy();
	}

	const shaka = await loadShaka();

	if (!shaka.Player.isBrowserSupported()) {
		throw new Error('Browser not supported');
	}

	const player = new shaka.Player() as ShakaPlayer;
	const config = options.config ?? buildPlayerConfig(isPreload);

	player.configure(config);

	const errorHandler = (event: any) => {
		// Shaka error handler installed but errors are handled gracefully
	};
	player.addEventListener('error', errorHandler);

	try {
		await player.attach(videoElement);
	} catch (err) {
		throw err;
	}

	try {
		await player.load(videoUrl, options.startTime);
	} catch (err) {
		throw err;
	}

	(videoElement as HTMLVideoElement & { shaka?: any }).shaka = player;
	return player;
}

export async function destroyPlayer(
	player: ShakaPlayer | null,
	videoElement?: HTMLVideoElement | null
): Promise<void> {
	if (!player) return;

	// Remove error listener if it was attached
	const errorHandler = (player as any).__errorHandler;
	if (errorHandler) {
		try {
			player.removeEventListener('error', errorHandler);
		} catch {
			// Ignore removal errors
		}
	}

	try {
		// Unload first to immediately cancel all in-flight network requests
		// (segment downloads, manifest fetches, etc.)
		await player.unload();
	} catch {
		// Ignore unload errors
	}

	try {
		await player.destroy();
	} catch {
		// Ignore destroy errors
	}

	// Clean up Shaka reference from video element to avoid DataCloneError on iOS
	// The Shaka Player instance contains non-cloneable closures/network handlers
	if (videoElement) {
		try {
			delete (videoElement as HTMLVideoElement & { shaka?: any }).shaka;
		} catch {
			// Ignore deletion errors
		}
	}
}

export { clearVideoCache } from '~/composables/useHlsPlayer/shakaLoader';
